import { type Models as M, Errors as E } from "@lux-ai/api-interface"
import { getContext, hasContext, setContext } from "svelte"

const PROGRESS_INTERVAL = 1000 * 10 // 5 sec
const PROGRESS_CONTROLLER_CTX_KEY = "lux-ai-progress-controller"

export function setProgressController(ctrl: ProgressController) {
	setContext(PROGRESS_CONTROLLER_CTX_KEY, ctrl)
}

export function getProgressController() {
	return getContext<ProgressController>(PROGRESS_CONTROLLER_CTX_KEY)
}

type GenerationId = M.ThemeGeneration["id"]

export class ProgressController {
	intervals: { [index: GenerationId]: number } = {}
	progress: { [index: GenerationId]: M.GenerationProgress["progress"] } = $state({})
	errors: { [index: GenerationId]: string } = $state({})

	clearProgressHandler = (generationId: GenerationId) => {
		clearInterval(this.intervals[generationId])
		delete this.intervals[generationId]
	}

	stopListening = () => {
		for (const interval of Object.values(this.intervals)) [clearInterval(interval)]
	}

	setErrorMessage(genId: GenerationId, errorMessage?: string) {
		if (!errorMessage && genId in this.errors) delete this.errors[genId]
		this.errors[genId] = errorMessage
	}

	/**
	 *
	 * @param generationId
	 * @param getProgress async method which returns the progress of the relevant generation
	 * * @throws — {E.MissingError} if the id does not map to a flow
	 * * @throws — {E.FailedRenderError} if the scene render failed
	 * * @throws — {E.FailedGenerationError} if the ai video generation failed
	 * @param onComplete
	 */
	createProgressHandler = (
		generationId: GenerationId,
		getProgress: () => Promise<number>,
		onComplete: () => void,
	) => {
		if (this.intervals[generationId]) clearInterval(this.intervals[generationId])

		const run = async () => {
			let hasCompleted = false
			try {
				this.progress[generationId] = await getProgress()

				console.log(`got progress for ${generationId}: ${this.progress[generationId]}`)
				if (this.progress[generationId] >= 1) {
					this.progress[generationId] = 1
					this.clearProgressHandler(generationId)
					onComplete()
					hasCompleted = true
					return
				}

				// ensure stopListening hasn't been called yet
				if (this.intervals[generationId] && !hasCompleted) {
					this.intervals[generationId] = setTimeout(run, PROGRESS_INTERVAL)
				}
			} catch (e) {
				console.error(`Error getting progress for ${generationId}...`)
				console.error(e)

				if (e instanceof E.MissingError) this.setErrorMessage(generationId, e.message)
				else if (e instanceof E.FailedRenderError) this.setErrorMessage(generationId, e.message)
				else if (e instanceof E.FailedGenerationError) this.setErrorMessage(generationId, e.message)
				else this.setErrorMessage(generationId, "Unable to get progress...")
			}
		}

		this.intervals[generationId] = 1
		run()
	}
}
